import React from 'react'
import useSWR from 'swr'

import { getInvoices, getClients } from '../../api'
import StatusFilterCheckboxes from './StatusFilterCheckboxes'
import ClientSelector from '../clients/selector'
import SimpleList, { type IssueDateSort } from './simpleList'
import DateRangeFilter, { type DateRangeValue } from './DateRangeFilter'
import { InvoiceStatus } from '../../types'
import Spinner from '../ui/spinner'
import Button, { ButtonSize, ButtonVariant } from '../ui/button'
import useScreenSize from '../../context/screenSize'
import {
  clearInvoiceListFilters,
  filtersDifferFromDefaults,
  getDefaultInvoiceListFilters,
  loadInvoiceListFilters,
  saveInvoiceListFilters,
} from '../../lib/invoiceListFilterStorage'
import { ReactComponent as ClearIcon } from '../../static/images/clear.svg'
import './styles.scss'

const DUE_STATUSES = [InvoiceStatus.OPEN, InvoiceStatus.OVERDUE]

function getEmptyStateMessage(
  statusFilter: string[],
  clientId?: string,
  clientName?: string
): string {
  const onlyDue = statusFilter.length > 0 && statusFilter.every((s) => DUE_STATUSES.includes(s as InvoiceStatus))
  const onlyDraft = statusFilter.length === 1 && statusFilter[0] === InvoiceStatus.DRAFT
  const hasDraft = statusFilter.includes(InvoiceStatus.DRAFT)
  const hasDue = statusFilter.some((s) => DUE_STATUSES.includes(s as InvoiceStatus))
  const hasPaid = statusFilter.includes(InvoiceStatus.PAID)

  let base: string
  if (onlyDraft) {
    base = 'No draft invoices found'
  } else if (onlyDue) {
    base = 'No open or overdue invoices found'
  } else if (hasDue && hasPaid) {
    base = 'No invoices created yet'
  } else if (hasPaid) {
    base = 'No paid invoices found'
  } else if (hasDraft && hasDue) {
    base = 'No draft, open or overdue invoices found'
  } else {
    base = 'No invoices found'
  }

  if (clientId) {
    const suffix = clientName ? ` for client "${clientName}"` : ' for the selected client'
    base += suffix
  }
  return base
}

/**
   * Build filters url from the filters state
   */
const API_QUERY_FILTER_KEYS = new Set(['status', 'clientId', 'period', 'from', 'to'])

const getFilters = (filters: {[x: string]: any}) => {
  const filterKeys = Object.keys(filters).filter((key) => API_QUERY_FILTER_KEYS.has(key))
  if(filterKeys.length === 0) {
    return ''
  }
  const filterString = filterKeys.map((key: string) => {
    const val = Array.isArray(filters[key]) ? filters[key].join(',') : filters[key]
    return `${key}=${val}`
  }).join('&')
  return `?${filterString}`
}

type InvoiceListProps = {
  /** When set, filter by this client and hide the client selector */
  fixedClientId?: string
}

/**
 * Shows a list of invoices.
 * Looks like a table with the following columns:
 * Issue Date,
 * Code,
 * Client,
 * Amount,
 * Status: Open, Paid, Overdue
 * Activity: Draft, Email Sent, Client Viewed
 * @returns
 */
const InvoiceList: React.FC<InvoiceListProps> = ({ fixedClientId }) => {
  const [filters, setFilters] = React.useState<{[x: string]: any}>(() => {
    const defaults = getDefaultInvoiceListFilters(fixedClientId)
    const stored = loadInvoiceListFilters(fixedClientId)
    if (!stored) return defaults
    const { issueDateSort: _issueDateSort, ...apiFilters } = stored
    return {
      ...defaults,
      ...apiFilters,
      ...(fixedClientId ? { clientId: fixedClientId } : {}),
    }
  })
  const [issueDateSort, setIssueDateSort] = React.useState<IssueDateSort>(() => {
    const stored = loadInvoiceListFilters(fixedClientId)
    return stored?.issueDateSort ?? 'asc'
  })
  const effectiveFilters = fixedClientId ? { ...filters, clientId: fixedClientId } : filters
  const { data, isLoading, mutate } = useSWR(`/api/invoices${getFilters(effectiveFilters)}`, getInvoices)
  const { data: clients } = useSWR(
    effectiveFilters.clientId ? '/api/clients' : null,
    getClients
  )
  const selectedClientName = effectiveFilters.clientId && clients
    ? clients.find((c: { _id: string; tradingName?: string }) => c._id === effectiveFilters.clientId)?.tradingName
    : undefined

  const handleFilterChange = (event: any) => {
    const { name, value } = event.target
    if(!value || value === '' || (name === 'period' && value === 'all')) {
      const newFilters = {...filters}
      delete newFilters[name]
      return setFilters(newFilters)
    } else {
      setFilters({...filters, [name]: value})
    }
  }

  const handleStatusChange = (statuses: string[]) => {
    if (statuses.length === 0) {
      const newFilters = {...filters}
      delete newFilters.status
      return setFilters(newFilters)
    }
    setFilters({...filters, status: statuses})
  }

  const handleDateRangeChange = (range: DateRangeValue) => {
    const newFilters = { ...filters }
    delete newFilters.period
    delete newFilters.from
    delete newFilters.to
    if (range.from && range.to) {
      newFilters.from = range.from
      newFilters.to = range.to
    } else if (range.period && range.period !== 'all') {
      newFilters.period = range.period
    }
    setFilters(newFilters)
  }

  const dateRangeValue: DateRangeValue = {
    ...(effectiveFilters.period ? { period: effectiveFilters.period } : {}),
    ...(effectiveFilters.from ? { from: effectiveFilters.from } : {}),
    ...(effectiveFilters.to ? { to: effectiveFilters.to } : {}),
  }

  React.useEffect(() => {
    const effective = fixedClientId ? { ...filters, clientId: fixedClientId } : filters
    saveInvoiceListFilters(
      {
        status: effective.status,
        clientId: effective.clientId,
        period: effective.period,
        from: effective.from,
        to: effective.to,
        issueDateSort,
      },
      fixedClientId,
    )
  }, [filters, issueDateSort, fixedClientId])

  const handleClearFilters = () => {
    const defaults = getDefaultInvoiceListFilters(fixedClientId)
    setFilters(defaults)
    setIssueDateSort('asc')
    clearInvoiceListFilters(fixedClientId)
  }

  const showClearFilters = filtersDifferFromDefaults(
    {
      status: effectiveFilters.status ?? getDefaultInvoiceListFilters(fixedClientId).status,
      clientId: effectiveFilters.clientId,
      period: effectiveFilters.period,
      from: effectiveFilters.from,
      to: effectiveFilters.to,
    },
    issueDateSort,
    fixedClientId,
  )

  const { isMobile } = useScreenSize()
  const [filtersOpen, setFiltersOpen] = React.useState(!isMobile)

  return (
    <div className='invoice-list-panel'>
      <div className={`invoice-list-panel__filters${filtersOpen ? '' : ' invoice-list-panel__filters--collapsed'}`}>
        <button
          type='button'
          className='invoice-list-panel__filters-toggle'
          onClick={() => setFiltersOpen((open) => !open)}
          aria-expanded={filtersOpen}
        >
          <span className='invoice-list-panel__filters-label'>Filters</span>
          <span className='invoice-list-panel__filters-chevron' aria-hidden />
        </button>
        {filtersOpen && (
          <div className='invoice-list-panel__filters-body'>
            <InvoiceFilters
              statusFilter={effectiveFilters.status ?? []}
              clientId={effectiveFilters.clientId}
              dateRange={dateRangeValue}
              onStatusChange={handleStatusChange}
              onChange={handleFilterChange}
              onDateRangeChange={handleDateRangeChange}
              hideClientFilter={!!fixedClientId}
            />
            {showClearFilters && (
              <ClearFiltersButton onClick={handleClearFilters} />
            )}
          </div>
        )}
      </div>
      <div className='invoice-list-panel__body'>
        {(isLoading) && <Spinner />}
        {!isLoading && data?.length === 0 && (
          <div className='invoice-list-empty-state'>
            <p className='invoice-list-empty'>
              {getEmptyStateMessage(
                effectiveFilters.status ?? [],
                effectiveFilters.clientId,
                selectedClientName
              )}
            </p>
            {showClearFilters && (
              <ClearFiltersButton
                onClick={handleClearFilters}
                className='invoice-list-panel__filters-clear--centered'
              />
            )}
          </div>
        )}
        {!isLoading && data?.length > 0 && (
          <SimpleList
            key={getFilters(effectiveFilters)}
            invoices={data}
            refreshInvoices={mutate}
            statusFilter={effectiveFilters.status ?? []}
            issueDateSort={issueDateSort}
            onIssueDateSortChange={setIssueDateSort}
          />
        )}
      </div>
    </div>
  )
}

const ClearFiltersButton: React.FC<{ onClick: () => void; className?: string }> = ({
  onClick,
  className,
}) => (
  <div className={['invoice-list-panel__filters-clear', className].filter(Boolean).join(' ')}>
    <Button
      type='button'
      variant={ButtonVariant.PLAIN}
      size={ButtonSize.SMALL}
      icon={<ClearIcon width={20} height={20} />}
      iconPosition='start'
      onClick={onClick}
      aria-label='Clear all filters'
    >
      Clear filters
    </Button>
  </div>
)

const InvoiceFilters = ({
  statusFilter,
  clientId,
  dateRange,
  onStatusChange,
  onChange,
  onDateRangeChange,
  hideClientFilter,
}: {
  statusFilter: string[]
  clientId?: string
  dateRange: DateRangeValue
  onStatusChange: (statuses: string[]) => void
  onChange: (event: any) => void
  onDateRangeChange: (value: DateRangeValue) => void
  hideClientFilter?: boolean
}) => {
  return (
    <div>
      <div className='row justify-content-between row-gap-4'>
        <div className='col-12 col-md-6 col-lg-6'>
          <StatusFilterCheckboxes
            selectedStatuses={statusFilter}
            onStatusChange={onStatusChange}
          />
        </div>
        {!hideClientFilter && (
          <div className='col-12 col-md-6 col-lg-3'>
            <ClientSelector
              placeHolder=''
              size='sm'
              onChange={onChange}
              showAll
              isClearable
              value={clientId}
            />
          </div>
        )}
        <div className='col-12 col-md-6 col-lg-3'>
          <DateRangeFilter value={dateRange} onChange={onDateRangeChange} />
        </div>
      </div>
    </div>
  )
}

export default InvoiceList
